import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowUp } from 'lucide-react';

import { useFeatures } from '../../../../context/FeatureContext';

/**
 * Översikt masthead: a Swedish time-of-day greeting and — for tenants with the
 * AI assistant — a composer that hands off to Finn.
 *
 * The composer deliberately owns no chat state. Submitting navigates to
 * /:companyId/ai-assistant with the text in router state; the chat page sends
 * it as soon as its socket is up (see BookkeepingChatInterface). Running a
 * second socket + transcript here would duplicate ~2000 lines of the chat page
 * for a control the user leaves within a second.
 */

/** 05–11 morgon · 12–17 eftermiddag · 18–04 kväll. */
const greetingForHour = (hour) => {
    if (hour >= 5 && hour < 12) return 'God morgon';
    if (hour >= 12 && hour < 18) return 'God eftermiddag';
    return 'God kväll';
};

/** ms until the next greeting boundary (05:00 / 12:00 / 18:00), local time. */
const msUntilNextGreetingChange = (now) => {
    const boundaries = [5, 12, 18];
    const next = new Date(now);
    const hour = now.getHours();
    const upcoming = boundaries.find((b) => b > hour);
    if (upcoming === undefined) {
        next.setDate(next.getDate() + 1);
        next.setHours(boundaries[0], 0, 0, 0);
    } else {
        next.setHours(upcoming, 0, 0, 0);
    }
    return next.getTime() - now.getTime();
};

const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// One prompt per corner of the team's turf, mirroring the chat page's own
// quick-start set so the two surfaces don't suggest different things.
const SUGGESTIONS = [
    'Vilka kundfakturor är obetalda?',
    'Hur gick förra månaden?',
    'Vad ska jag göra den här veckan?',
];

const DashboardWelcome = ({ companyId }) => {
    const history = useHistory();
    const { features } = useFeatures();
    const user = useSelector((state) => state.auth.user);

    // Re-render exactly on the boundary rather than ticking every minute: the
    // greeting is the only thing that changes and it changes three times a day.
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const timer = setTimeout(() => setNow(new Date()), msUntilNextGreetingChange(now));
        return () => clearTimeout(timer);
    }, [now]);

    const [message, setMessage] = useState('');
    const textareaRef = useRef(null);

    const hasAssistant = features?.ai_assistant === true;
    const firstName = user?.firstName?.trim();

    const greeting = useMemo(() => {
        const base = greetingForHour(now.getHours());
        return firstName ? `${base}, ${firstName}` : base;
    }, [now, firstName]);

    const dateLabel = useMemo(
        () => capitalise(now.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })),
        [now]
    );

    const submit = useCallback(
        (text) => {
            const trimmed = (text ?? '').trim();
            if (!trimmed) return;
            history.push(`/${companyId}/ai-assistant`, { initialMessage: trimmed });
        },
        [companyId, history]
    );

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit(message);
        }
    };

    // Grow with the content up to ~4 lines, then scroll — same behaviour as the
    // chat composer so the control doesn't feel different between the two.
    const onChange = (e) => {
        setMessage(e.target.value);
        const el = textareaRef.current;
        if (el) {
            el.style.height = 'auto';
            el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
        }
    };

    return (
        <section className="dashboard-welcome">
            <p className="dashboard-welcome__date">{dateLabel}</p>
            <h1 className="dashboard-welcome__greeting">{greeting}</h1>

            {hasAssistant && (
                <div className="dashboard-welcome__composer-wrap">
                    <form
                        className="dashboard-composer"
                        onSubmit={(e) => {
                            e.preventDefault();
                            submit(message);
                        }}
                    >
                        <textarea
                            ref={textareaRef}
                            className="dashboard-composer__input"
                            value={message}
                            onChange={onChange}
                            onKeyDown={onKeyDown}
                            rows={2}
                            placeholder="Fråga Finn om din ekonomi…"
                            aria-label="Fråga Finn om din ekonomi"
                        />
                        <div className="dashboard-composer__actions">
                            <button
                                type="submit"
                                className="dashboard-composer__send"
                                disabled={!message.trim()}
                                aria-label="Skicka till Finn"
                            >
                                <ArrowUp size={16} strokeWidth={2.25} />
                            </button>
                        </div>
                    </form>

                    <div className="dashboard-composer__suggestions">
                        {SUGGESTIONS.map((s) => (
                            <button key={s} type="button" className="dashboard-composer__chip" onClick={() => submit(s)}>
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};

export default DashboardWelcome;
